/**
 * Shared relative-time formatting.
 *
 * This is a plain (non-React) lib, so it must stay free of hardcoded
 * user-facing copy (project rule #7: Italian lives only in locales/it.json).
 * The localized strings are therefore INJECTED: the caller passes the same
 * `t(key, params)` translator it already has from `useCore().i18n`, and this
 * function only decides WHICH key and WHICH numeric value to render. The
 * Italian phrasing ("ora", "5 min fa", …) lives in it.json under `time.relative.*`.
 *
 * Output is intentionally identical to the previous inline implementations in
 * NotificationBell and NotificationsSettings ("ora" / "5 min fa" / "3 h fa" /
 * "2 g fa", and "" for a missing timestamp).
 */

/** Structural type matching `I18nService.t`; avoids importing from packages. */
export type Translate = (key: string, params?: Readonly<Record<string, string | number>>) => string;

/** Firestore `Timestamp` shape → ms epoch, or null when absent. */
export function timestampToMillis(
  ts: { seconds: number; nanoseconds: number } | undefined,
): number | null {
  if (!ts) return null;
  return ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1_000_000);
}

/**
 * Compact relative time from a ms epoch (or null). Returns "" when `ms` is null
 * so empty timestamps render nothing, exactly as before.
 */
export function relativeTime(ms: number | null, t: Translate): string {
  if (ms === null) return "";
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return t("time.relative.now");
  if (min < 60) return t("time.relative.minutes", { value: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t("time.relative.hours", { value: h });
  const d = Math.floor(h / 24);
  return t("time.relative.days", { value: d });
}
